import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

// SVG Icons
const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
        <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
);

// Password Strength Logic
function getPasswordStrength(pwd) {
    let score = 0;
    if (pwd.length >= 8) score += 25;
    if (pwd.length >= 12) score += 15;
    if (/[A-Z]/.test(pwd)) score += 20;
    if (/[a-z]/.test(pwd)) score += 10;
    if (/[0-9]/.test(pwd)) score += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score += 15;
    return Math.min(score, 100);
}

function getStrengthColor(pwd) {
    const s = getPasswordStrength(pwd);
    if (s < 40) return 'var(--error)';
    if (s < 70) return 'var(--warning)';
    return 'var(--success)';
}

function getStrengthLabel(pwd) {
    const s = getPasswordStrength(pwd);
    if (s < 40) return 'Weak';
    if (s < 70) return 'Medium';
    return 'Strong';
}

function Register() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'WORKER'
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [registered, setRegistered] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 1. Validate Passwords Match
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            // 2. Send data to backend (Including the new 'name' field)
            await axios.post(`${API_URL}/auth/register`, {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                role: formData.role
            });
            
            setRegistered(true);
            toast.success('Registration successful! Check your email.');
            
        } catch (err) {
            toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
        }
        
        setLoading(false);
    };

    // Success Page View
    if (registered) {
        return (
            <div className="auth-page">
                <div className="auth-card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>📧</div>
                    <h2 className="auth-title">Check Your Email</h2>
                    <p className="auth-subtitle">We've sent a verification link to:</p>
                    <p style={{ color: 'var(--text)', fontSize: '16px', fontWeight: '500', marginBottom: '20px' }}>
                        {formData.email}
                    </p>
                    <p className="auth-subtitle">Click the link in the email to verify your account.</p>
                    <button onClick={() => navigate('/')} className="btn-primary" style={{ width: '100%', marginTop: '16px' }}>
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // Main Register Form View
    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-brand fade-up delay-1">
                    <h1 className="auth-logo">infloir</h1>
                    <p className="auth-tagline">Global Task Marketplace</p>
                </div>

                <div className="fade-up delay-2">
                    <h2 className="auth-title">Create an account</h2>
                    <p className="auth-subtitle">Join the marketplace today</p>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Role Selector */}
                    <div className="form-field fade-up delay-3">
                        <label className="form-label">I want to join as a:</label>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button type="button" onClick={() => setFormData({ ...formData, role: 'WORKER' })} className={formData.role === 'WORKER' ? 'btn-primary' : 'btn-secondary'} style={{ flex: 1, padding: '10px', fontSize: '13px' }}>
                                Worker
                            </button>
                            <button type="button" onClick={() => setFormData({ ...formData, role: 'BUSINESS' })} className={formData.role === 'BUSINESS' ? 'btn-primary' : 'btn-secondary'} style={{ flex: 1, padding: '10px', fontSize: '13px' }}>
                                Business
                            </button>
                        </div>
                    </div>

                    {/* Full Name */}
                    <div className="form-field fade-up delay-3">
                        <label className="form-label" htmlFor="name">Full Name</label>
                        <input id="name" name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange} required />
                    </div>

                    {/* Email */}
                    <div className="form-field fade-up delay-4">
                        <label className="form-label" htmlFor="email">Email</label>
                        <input id="email" name="email" type="email" placeholder="you@example.com" autoComplete="email" value={formData.email} onChange={handleChange} required />
                    </div>

                    {/* Password */}
                    <div className="form-field fade-up delay-5">
                        <label className="form-label" htmlFor="password">Password</label>
                        <div className="input-wrap">
                            <input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="Create a strong password" value={formData.password} onChange={handleChange} required minLength="8" />
                            <button type="button" className="icon-btn" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                            </button>
                        </div>
                        
                        {/* Strength Meter */}
                        {formData.password && (
                            <div style={{ marginTop: '8px' }}>
                                <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${getPasswordStrength(formData.password)}%`, backgroundColor: getStrengthColor(formData.password), transition: 'all 0.3s ease' }}></div>
                                </div>
                                <p style={{ fontSize: '11px', color: getStrengthColor(formData.password), marginTop: '4px', textAlign: 'right', fontWeight: '500' }}>
                                    {getStrengthLabel(formData.password)}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className="form-field fade-up delay-6">
                        <label className="form-label" htmlFor="confirmPassword">Confirm Password</label>
                        <div className="input-wrap">
                            <input id="confirmPassword" name="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} required />
                        </div>
                    </div>

                    <div className="fade-up delay-6">
                        <button type="submit" className="btn-primary auth-submit" disabled={loading}>
                            {loading && <span className="spinner" />}
                            {loading ? 'Creating account...' : 'Create Account'}
                        </button>
                    </div>
                </form>

                <div className="fade-up delay-6">
                    <p className="auth-footer-text" style={{ marginTop: '24px' }}>
                        Already have an account? <Link to="/login">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Register;